import type { JobListQuery } from '@chukua/common';
import { Alert, Button, Table, type TableColumnsType } from 'antd';
import { Inbox, SearchX, ShieldAlert } from 'lucide-react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ListSearch } from '../../../shared/components/ListSearch';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { JobListItem } from '../../jobs/jobs.api';
import type { JobQueueState } from '../hooks/use-job-queue';

interface JobQueueTableProps {
  state: JobQueueState;
  columns: TableColumnsType<JobListItem>;
  list: ListQueryControls<JobListQuery>;
  isRefreshing: boolean;
  skippedCount: number;
  isShowingSkipped: boolean;
  onToggleSkipped: () => void;
  onRetry: () => void;
}

/** The queue panel: every state of it, so the page only places panels. Without `job.view` it is a notice and the other panels still load. */
export function JobQueueTable({ state, columns, list, isRefreshing, skippedCount, isShowingSkipped, onToggleSkipped, onRetry }: JobQueueTableProps) {
  if (state.status === 'loading') return <TableSkeleton label="Loading the job queue" withSearch rows={5} />;
  if (state.status === 'forbidden') {
    return (
      <Alert
        type="info"
        showIcon
        message="You can't see the job queue"
        description="Viewing jobs needs the job.view permission. Ask an administrator if you need it."
      />
    );
  }
  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load the job queue" description="Something went wrong loading the jobs waiting for a crew.">
        <Button onClick={onRetry}>Try again</Button>
      </EmptyState>
    );
  }
  const { page } = state;
  const isQueueEmpty = page.total === 0 && !list.isFiltered && skippedCount === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <ListSearch value={list.query.q} placeholder="Search job number or customer" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
        {skippedCount > 0 && (
          <Button type="link" onClick={onToggleSkipped}>
            {isShowingSkipped ? 'Hide skipped' : `Show skipped (${skippedCount})`}
          </Button>
        )}
      </div>
      {isQueueEmpty ? (
        <EmptyState icon={Inbox} title="Nothing waiting for a crew" description="Confirmed and planned jobs show here until their trips are dispatched." />
      ) : (
        <Table
          rowKey="id"
          dataSource={page.items}
          columns={columns}
          loading={isRefreshing}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <NoMatchingJobs isFiltered={list.isFiltered} /> }}
          {...list.tableProps<JobListItem>(page.total)}
        />
      )}
    </div>
  );
}

function NoMatchingJobs({ isFiltered }: { isFiltered: boolean }) {
  if (!isFiltered) return <EmptyState icon={Inbox} title="Every queued job is skipped" description="Show skipped jobs to bring one back." />;
  return <EmptyState icon={SearchX} title="No matching jobs" description="No queued jobs match this search or these filters." />;
}
